/**
 * ICS Calendar File Generator
 *
 * Generates RFC 5545 compliant .ics files for calendar integration.
 * Supports Google Calendar, Apple Calendar, Outlook, and other iCal clients.
 *
 * Epic: MVP 1.21.5 - Dashboard Completion
 * Story: 4.2 - Calendar Export Functionality
 *
 * @version 1.0
 * @created 2026-01-17
 *
 * RFC 5545 Specification: https://tools.ietf.org/html/rfc5545
 */

/**
 * Format a date to RFC 5545 format (YYYYMMDDTHHMMSSZ)
 * @param {Date|string} date - Date to format
 * @returns {string} Formatted date string
 */
function formatICSDate(date) {
  const d = new Date(date);

  // RFC 5545 format: YYYYMMDDTHHMMSSZ (UTC)
  const year = d.getUTCFullYear();
  const month = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  const hours = String(d.getUTCHours()).padStart(2, '0');
  const minutes = String(d.getUTCMinutes()).padStart(2, '0');
  const seconds = String(d.getUTCSeconds()).padStart(2, '0');

  return `${year}${month}${day}T${hours}${minutes}${seconds}Z`;
}

/**
 * Escape special characters for ICS format
 * @param {string} str - String to escape
 * @returns {string} Escaped string
 */
function escapeICSText(str) {
  if (!str) {return '';}

  return str
    .replace(/\\/g, '\\\\')  // Escape backslashes
    .replace(/;/g, '\\;')    // Escape semicolons
    .replace(/,/g, '\\,')    // Escape commas
    .replace(/\n/g, '\\n')   // Escape newlines
    .replace(/\r/g, '');     // Remove carriage returns
}

/**
 * Fold long lines to 75 characters per RFC 5545
 * @param {string} line - Line to fold
 * @returns {string} Folded line
 */
function foldLine(line) {
  if (line.length <= 75) {return line;}

  const lines = [];
  let currentLine = line.substring(0, 75);
  let remaining = line.substring(75);

  lines.push(currentLine);

  while (remaining.length > 0) {
    currentLine = ' ' + remaining.substring(0, 74); // Continuation lines start with space
    remaining = remaining.substring(74);
    lines.push(currentLine);
  }

  return lines.join('\r\n');
}

/**
 * Generate RFC 5545 compliant .ics file content
 * @param {Object} trip - Trip/booking data
 * @param {string} trip.experience_title - Experience title
 * @param {string} trip.start_date - Trip date (ISO string)
 * @param {number} trip.duration_hours - Duration in hours
 * @param {string} trip.meeting_location_name - Meeting location
 * @param {number} trip.meeting_location_lat - Meeting latitude (optional)
 * @param {number} trip.meeting_location_lng - Meeting longitude (optional)
 * @param {string} trip.guide_first_name - Guide first name
 * @param {string} trip.guide_last_name - Guide last name
 * @param {string} trip.guide_phone - Guide phone (optional)
 * @param {string} trip.experience_description - Experience description (optional)
 * @returns {string} ICS file content
 */
export function generateICS(trip) {
  // Parse trip date
  const startDate = new Date(trip.start_date);

  // Calculate end date (start date + duration)
  const durationHours = trip.duration_hours || 4; // Default 4 hours
  const endDate = new Date(startDate.getTime() + (durationHours * 60 * 60 * 1000));

  // Create alarm 1 day before
  const _alarmDate = new Date(startDate.getTime() - (24 * 60 * 60 * 1000));

  // Build location string
  let location = escapeICSText(trip.meeting_location_name || 'TBD');
  if (trip.meeting_location_lat && trip.meeting_location_lng) {
    location = `${location} (${trip.meeting_location_lat}, ${trip.meeting_location_lng})`;
  }

  // Build description
  const guideName = `${trip.guide_first_name} ${trip.guide_last_name}`;
  let description = `Outdoor adventure with ${guideName}`;

  if (trip.experience_description) {
    description += `\\n\\n${escapeICSText(trip.experience_description)}`;
  }

  if (trip.guide_phone) {
    description += `\\n\\nGuide Contact: ${trip.guide_phone}`;
  }

  description += `\\n\\nDuration: ${durationHours} hours`;

  // Generate unique UID (using booking ID if available)
  const uid = trip.id || `trip-${Date.now()}@outdoorguides.com`;

  // Current timestamp for DTSTAMP
  const now = formatICSDate(new Date());

  // Build ICS content (RFC 5545 compliant)
  const icsLines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//OutdoorGuides//Trip Calendar//EN',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    'X-WR-CALNAME:OutdoorGuides Trip',
    'X-WR-TIMEZONE:UTC',
    'BEGIN:VEVENT',
    `UID:${uid}`,
    `DTSTAMP:${now}`,
    `DTSTART:${formatICSDate(startDate)}`,
    `DTEND:${formatICSDate(endDate)}`,
    foldLine(`SUMMARY:${escapeICSText(trip.experience_title || 'Outdoor Adventure')}`),
    foldLine(`DESCRIPTION:${description}`),
    foldLine(`LOCATION:${location}`),
    'STATUS:CONFIRMED',
    'TRANSP:OPAQUE',
    'SEQUENCE:0',
    'BEGIN:VALARM',
    'TRIGGER:-PT24H',  // 24 hours before
    'ACTION:DISPLAY',
    foldLine(`DESCRIPTION:Reminder: ${escapeICSText(trip.experience_title || 'Outdoor Adventure')} tomorrow`),
    'END:VALARM',
    'END:VEVENT',
    'END:VCALENDAR'
  ];

  // Join with CRLF (required by RFC 5545)
  return icsLines.join('\r\n');
}

/**
 * Trigger browser download of ICS file
 * @param {string} icsContent - ICS file content
 * @param {string} filename - Filename (without extension)
 */
export function downloadICS(icsContent, filename = 'outdoor-trip') {
  // Create blob with proper MIME type
  const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });

  // Create download link
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = `${filename}.ics`;

  // Trigger download
  document.body.appendChild(link);
  link.click();

  // Cleanup
  document.body.removeChild(link);
  URL.revokeObjectURL(link.href);
}

/**
 * Generate Google Calendar add event URL
 * @param {Object} trip - Trip/booking data
 * @returns {string} Google Calendar URL
 */
export function generateGoogleCalendarLink(trip) {
  const startDate = new Date(trip.start_date);
  const durationHours = trip.duration_hours || 4;
  const endDate = new Date(startDate.getTime() + (durationHours * 60 * 60 * 1000));

  // Format dates for Google Calendar (YYYYMMDDTHHMMSSZ)
  const formatGoogleDate = (date) => {
    return formatICSDate(date);
  };

  // Build location string
  let location = trip.meeting_location_name || 'TBD';
  if (trip.meeting_location_lat && trip.meeting_location_lng) {
    location = `${location} (${trip.meeting_location_lat}, ${trip.meeting_location_lng})`;
  }

  // Build description
  const guideName = `${trip.guide_first_name} ${trip.guide_last_name}`;
  let description = `Outdoor adventure with ${guideName}`;

  if (trip.experience_description) {
    description += `\n\n${trip.experience_description}`;
  }

  if (trip.guide_phone) {
    description += `\n\nGuide Contact: ${trip.guide_phone}`;
  }

  description += `\n\nDuration: ${durationHours} hours`;

  // Build Google Calendar URL
  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: trip.experience_title || 'Outdoor Adventure',
    dates: `${formatGoogleDate(startDate)}/${formatGoogleDate(endDate)}`,
    details: description,
    location: location,
    trp: 'true' // Show event details page
  });

  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}

/**
 * Export trip to calendar (auto-detect method)
 * @param {Object} trip - Trip/booking data
 * @param {string} method - Export method: 'download' (default) or 'google'
 */
export function exportToCalendar(trip, method = 'download') {
  if (method === 'google') {
    const url = generateGoogleCalendarLink(trip);
    window.open(url, '_blank');
  } else {
    // Default: Download .ics file
    const icsContent = generateICS(trip);
    const filename = `outdoor-trip-${trip.id || Date.now()}`;
    downloadICS(icsContent, filename);
  }
}
